/**
 * Progress chart insights — short, motivating, data-aware blurbs.
 * Each generator returns: { headline, body, tone, delta?, accent }
 * tone:  "up" | "flat" | "down" | "neutral"
 * accent: hex color used for trend pill
 */

const TONE_LABEL = {
  up: "Trending up",
  flat: "Steady",
  down: "Reset moment",
  neutral: "Insight",
};

const TONE_COLOR = {
  up: "#4ade80",
  flat: "#22d3ee",
  down: "#FBBF24",
  neutral: "#a78bfa",
};

function avg(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function splitHalves(values) {
  const n = values.length;
  if (n < 2) return [values, values];
  const mid = Math.floor(n / 2);
  return [values.slice(0, mid), values.slice(mid)];
}

function pctChange(prev, next) {
  if (!prev) return next ? 100 : 0;
  return ((next - prev) / prev) * 100;
}

function bestDay(data, key) {
  if (!data.length) return null;
  return data.reduce((m, d) => (d[key] > (m?.[key] ?? -Infinity) ? d : m), null);
}

/* ---------- Generators ---------- */

export function totalInsight(data) {
  if (!data.length) return null;
  const vals = data.map((d) => d.Total);
  const total = vals.reduce((a, b) => a + b, 0);
  const days = data.length;
  const dailyAvg = Math.round(total / days);
  const [a, b] = splitHalves(vals);
  const change = pctChange(avg(a), avg(b));
  const best = bestDay(data, "Total");
  let tone = "flat";
  let headline = `Steady ${dailyAvg.toLocaleString()} steps/day`;
  let body = `${days} days tracked. Consistency builds the streak — keep showing up.`;
  if (change >= 8) {
    tone = "up";
    headline = `+${Math.round(change)}% vs early in the period`;
    body = `Your average is climbing — ${dailyAvg.toLocaleString()} steps/day. Best day was ${best?.date} (${best?.Total.toLocaleString()}).`;
  } else if (change <= -8) {
    tone = "down";
    headline = `${Math.round(change)}% softer this stretch`;
    body = `A lighter window — average ${dailyAvg.toLocaleString()}/day. Small reset tomorrow: one walk before noon.`;
  } else {
    body = `${days} days × ${dailyAvg.toLocaleString()} avg. Best day ${best?.date} hit ${best?.Total.toLocaleString()}.`;
  }
  return { headline, body, tone, delta: change, accent: TONE_COLOR[tone], label: TONE_LABEL[tone] };
}

export function activeVsPassiveInsight(data) {
  if (!data.length) return null;
  const active = data.reduce((a, x) => a + (x.Active || 0), 0);
  const passive = data.reduce((a, x) => a + (x.Passive || 0), 0);
  const total = active + passive;
  if (total === 0) {
    return {
      headline: "Awaiting first session",
      body: "Log a walk or passive timer and this chart will light up.",
      tone: "neutral",
      accent: TONE_COLOR.neutral,
      label: TONE_LABEL.neutral,
    };
  }
  const activePct = Math.round((active / total) * 100);
  const passivePct = 100 - activePct;
  const dayCount = data.length;
  const activeDaysLed = data.filter((d) => d.Active > d.Passive).length;
  let tone = "flat";
  let headline = `${activePct}% Active · ${passivePct}% Passive`;
  let body = `Balanced movement — active leads ${activeDaysLed}/${dayCount} days. Your body is moving AND aligning.`;
  if (activePct >= 65) {
    tone = "up";
    headline = `Active engine running at ${activePct}%`;
    body = `Big movement bias — protect this with one mobility set/day to keep injury risk down.`;
  } else if (passivePct >= 55) {
    tone = "flat";
    headline = `Passive minutes are stacking (${passivePct}%)`;
    body = `Proof you can earn points without leaving your desk. Bonus walk would push the ring full.`;
  }
  return { headline, body, tone, accent: TONE_COLOR[tone], label: TONE_LABEL[tone] };
}

export function postureScoreInsight(data) {
  if (!data.length) return null;
  const scores = data.map((d) => d.Score).filter((s) => s > 0);
  if (!scores.length) {
    return {
      headline: "Check in to see your score",
      body: "Daily check-ins feed your Posture Score™. Two days makes a curve.",
      tone: "neutral",
      accent: TONE_COLOR.neutral,
      label: TONE_LABEL.neutral,
    };
  }
  const latest = scores[scores.length - 1];
  const average = Math.round(avg(scores));
  const [a, b] = splitHalves(scores);
  const delta = Math.round(avg(b) - avg(a));
  const peak = Math.max(...scores);
  let tone = "flat";
  let headline = `Latest ${latest} · avg ${average}`;
  let body = `Posture Score™ holding. Peak so far: ${peak}.`;
  if (delta >= 3) {
    tone = "up";
    headline = `+${delta} pts vs early period`;
    body = `Your score is climbing — peak ${peak}. Movement + posture + consistency are compounding.`;
  } else if (delta <= -3) {
    tone = "down";
    headline = `${delta} pts softer recently`;
    body = `A small dip. Tip: a single check-in + 20-min walk usually claws back 4–6 points.`;
  } else if (latest >= 80) {
    tone = "up";
    headline = `Elite zone (${latest}/100)`;
    body = `You're operating in the top tier. Maintain with weekly Posture Sessions.`;
  }
  return { headline, body, tone, delta, accent: TONE_COLOR[tone], label: TONE_LABEL[tone] };
}

export function moodStressInsight(data) {
  if (!data.length) return null;
  const moods = data.map((d) => d.Mood).filter((m) => m > 0);
  const stress = data.map((d) => d.Stress).filter((s) => s > 0);
  if (!moods.length && !stress.length) {
    return {
      headline: "Check-ins unlock this view",
      body: "30-second daily check-in surfaces how your body is influencing your mind.",
      tone: "neutral",
      accent: TONE_COLOR.neutral,
      label: TONE_LABEL.neutral,
    };
  }
  const moodAvg = avg(moods).toFixed(1);
  const stressAvg = avg(stress).toFixed(1);
  const [ma, mb] = splitHalves(moods);
  const [sa, sb] = splitHalves(stress);
  const moodChange = avg(mb) - avg(ma);
  const stressChange = avg(sb) - avg(sa);

  let tone = "flat";
  let headline = `Mood ${moodAvg}/10 · Stress ${stressAvg}/10`;
  let body = `Tracking how your body shapes your mind — the data is already showing.`;
  if (moodChange > 0.4 && stressChange < -0.4) {
    tone = "up";
    headline = "Mood up, stress down";
    body = `+${moodChange.toFixed(1)} mood, ${stressChange.toFixed(1)} stress. Movement is literally rewiring your day.`;
  } else if (moodChange > 0.4) {
    tone = "up";
    headline = `Mood climbing (+${moodChange.toFixed(1)})`;
    body = `Your mood average lifted across the period. Walking is your antidepressant.`;
  } else if (stressChange < -0.4) {
    tone = "up";
    headline = `Stress easing (${stressChange.toFixed(1)})`;
    body = `Posture Sessions + passive minutes are lowering daily stress signals.`;
  } else if (moodChange < -0.4 || stressChange > 0.4) {
    tone = "down";
    headline = "Tougher window";
    body = `Today is a great day to do less, but better. 10-min walk + breathwork moves the needle most.`;
  } else {
    body = `Average mood ${moodAvg}, stress ${stressAvg}. Stable — and stability is itself a win.`;
  }
  return { headline, body, tone, accent: TONE_COLOR[tone], label: TONE_LABEL[tone] };
}
